import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useEffect, useMemo, useState } from 'react';
import { ReviewStep } from '@shared/domain/review';
import { useReviewContext } from '../context/ReviewContext';
import '../styles/review-stepper.scss';

const steps = [
  { label: 'Basic Info', id: ReviewStep.BASIC_REVIEW },
  { label: 'Entry Strategy', id: ReviewStep.ENTRY_STRATEGY },
  { label: 'Purchase Details', id: ReviewStep.PURCHASE_DETAILS },
  { label: 'Evaluation', id: ReviewStep.EVALUATION },
  { label: 'Legal Review', id: ReviewStep.LEGAL },
  { label: 'Negotiation and Procurement', id: ReviewStep.NEGOTIATION },
];

interface ReviewStepperProps {
  handleBack: (step: ReviewStep) => void;
  rfp: boolean;
  loading?: boolean;
}

function ReviewStepper(props: ReviewStepperProps) {
  const { handleBack, rfp, loading = false } = props;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { reviewStep, setReviewStep, setCurrentReview, lastStepSaved }
    = useReviewContext();

  const customSteps = useMemo(() => {
    return rfp ? [...steps, { label: 'RFPs', id: ReviewStep.RFP }] : steps;
  }, [rfp]);

  const [mobileIndex, setMobileIndex] = useState(0);

  useEffect(() => {
    const safeIndex = Math.min(reviewStep, customSteps.length - 1);
    setMobileIndex(safeIndex);
  }, [reviewStep, customSteps.length]);

  const maxReachable = Math.min(lastStepSaved ?? 0, customSteps.length - 1);

  const goToStep = (index: number, stepId: ReviewStep) => {
    if (loading) return;
    if (index > maxReachable) return;
    setCurrentReview(null);
    setReviewStep(index);
    handleBack(stepId);
  };

  if (isMobile) {
    const progressPct
      = ((reviewStep + 1) / customSteps.length) * 100;

    return (
      <div className="review-stepper-shell">
        <div className="review-stepper-mobile">
          <div className="review-stepper-mobile__head">
            <h3 className="review-stepper-mobile__title">
              {customSteps[mobileIndex]?.label ?? ''}
            </h3>
            <span className="review-stepper-mobile__counter">
              <strong>{mobileIndex + 1}</strong>
              {' / '}
              {customSteps.length}
            </span>
          </div>

          <div className="review-stepper-mobile__progress">
            <div
              className="review-stepper-mobile__progress-fill"
              style={{ width: `${progressPct}%` }}
            />
          </div>

          <div className="review-stepper-mobile__nav">
            <button
              type="button"
              className="review-stepper-mobile__btn"
              disabled={loading || mobileIndex === 0}
              onClick={() => {
                if (mobileIndex > 0) {
                  const prev = mobileIndex - 1;
                  setMobileIndex(prev);
                  goToStep(prev, customSteps[prev].id);
                }
              }}
            >
              <KeyboardArrowLeft />
              Back
            </button>
            <button
              type="button"
              className="review-stepper-mobile__btn"
              disabled={loading || mobileIndex >= maxReachable}
              onClick={() => {
                if (mobileIndex < maxReachable) {
                  const next = mobileIndex + 1;
                  setMobileIndex(next);
                  goToStep(next, customSteps[next].id);
                }
              }}
            >
              Next
              <KeyboardArrowRight />
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="review-stepper-shell">
      <ol className="review-stepper">
        {customSteps.map((step, i) => {
          const isComplete = i < reviewStep;
          const isActive = i === reviewStep;
          const isLocked = i > maxReachable;

          const stateClass = [
            'review-stepper__item',
            isActive && 'review-stepper__item--active',
            isComplete && 'review-stepper__item--complete',
            isLocked && 'review-stepper__item--locked',
            loading && 'review-stepper__item--loading',
          ]
            .filter(Boolean)
            .join(' ');

          return (
            <li key={step.id} style={{ flex: '1 1 0', listStyle: 'none' }}>
              <button
                type="button"
                className={stateClass}
                disabled={isLocked || loading}
                title={
                  loading
                    ? 'Loading…'
                    : isLocked
                      ? 'Step locked'
                      : 'Go to this step'
                }
                aria-current={isActive ? 'step' : undefined}
                onClick={() => goToStep(i, step.id)}
                onKeyDown={(e) => {
                  if ((e.key === 'Enter' || e.key === ' ') && !isLocked && !loading) {
                    e.preventDefault();
                    goToStep(i, step.id);
                  }
                }}
              >
                <span className="review-stepper__icon" aria-hidden="true">
                  {isComplete ? <CheckRoundedIcon /> : i + 1}
                </span>
                <span className="review-stepper__label">{step.label}</span>
              </button>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

export default ReviewStepper;
